import React, { Children, cloneElement, ReactNode } from 'react'

// Utils
import classnames from 'classnames'

// Hooks
import useActiveTab from './hooks/useActiveTab'

// Styles
import { trackClickEvent } from '@src/telemetry/track'

import { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import styles from './tabs.module.scss'

export interface TabProps {
  children: ReactNode
  id: string
  title: string | ReactNode
  active?: boolean
  headerClassName?: string
  panelClassName?: string
  tracker?: string
  trackingMetaData?: TrackingMetaData | undefined
}

export const Tab = ({ children, active, panelClassName }: TabProps) => {
  return (
    <div
      className={classnames(
        styles.tabs__tab_panel,
        {
          [styles.tabs__active_tab_panel]: active,
          [styles.tabs__inactive_tab_panel]: !active,
        },
        panelClassName,
      )}
    >
      {children}
    </div>
  )
}

export interface TabsProps {
  children?: React.ReactElement<TabProps> | (React.ReactElement<TabProps> | null)[]
  activeTabId?: string
  onChange?: (activeId: string) => void
  className?: string
  tracker?: string
  tabButtonWrapperClassname?: string
  activeTabHeaderClassName?: string
}

const Tabs = ({
  activeTabId: controlledActiveTabId,
  onChange,
  children,
  className,
  tracker,
  tabButtonWrapperClassname,
  activeTabHeaderClassName,
}: TabsProps) => {
  const classes = classnames(styles.tabs, className)
  const firstTabId = (Children.toArray(children)[0] as React.ReactElement<TabProps>).props.id

  const { handleChange, activeTabId } = useActiveTab({
    initialActiveTabId: firstTabId,
    controlledActiveTabId,
    onChange,
  })

  const handleTabClick = (id: string, tabTitle: string | ReactNode, trackingMetaData?: TrackingMetaData) => {
    handleChange(id)
    if (tracker) {
      trackClickEvent(
        TELEMETRY_EVENT_NAMES.BUTTON_CLICK,
        tracker
          ? {
              trackGA: true,
              trackInternal: true,
              category: 'navigation',
              action: 'click__tabs__tab',
              label: tabTitle,
              value: tracker,
              context: trackingMetaData?.context,
            }
          : undefined,
      )
    }
  }

  return (
    <div className={classes}>
      <div className={classnames(styles.tabs__button_wrap, 'tabButtonWrap', tabButtonWrapperClassname)}>
        {Children.map(children, tab => {
          if (!tab) return null
          return (
            <button
              key={tab.props.id}
              onClick={() => {
                handleTabClick(tab.props.id, tab.props.title, tab.props.trackingMetaData)
              }}
              className={`${classnames(styles.tabs__button, [
                {
                  [styles.tabs__active_tab]: activeTabId === tab.props.id,
                  [styles.tabs__inactive_tab]: activeTabId !== tab.props.id,
                },
                tab.props.headerClassName,
                activeTabId === tab.props.id && activeTabHeaderClassName,
              ])} ${activeTabId === tab.props.id ? 'activeTab' : 'inactiveTab'}`}
            >
              {tab.props.title}
            </button>
          )
        })}
      </div>
      {Children.map(children, tab => {
        if (!tab) return null

        return cloneElement(tab, {
          ...tab.props,
          active: activeTabId === tab.props.id,
        })
      })}
    </div>
  )
}

export interface TabPanel {
  tabId: string
  panel: ReactNode
}

export default Tabs
